<script setup>
import { SwSvgIcon, mainColors } from '@getswish/design-library-web';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { mapGetters, mapActions, useStore } from 'vuex';
import Icon from '@/components/CustomIcon.vue';

const dropdownOpen = ref(false);

const store = useStore();
const isOpen = computed(() => store.getters.common.isOpen);

const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value;
};

const closeDropdown = () => {
  dropdownOpen.value = false;
};

const route = useRoute();
watch(
  () => route.fullPath,
  () => {
    dropdownOpen.value = false;
  },
);
</script>

<template>
  <div>
    <div class="topbar">
      <img class="logo" src="@/assets/swish-logo-horizontal.svg" alt="Swish logotype" />

      <a @click="store.dispatch('common/toggleSidebar')">
        <SwSvgIcon name="menu" :color="mainColors.secondaryForeground" class="toggle-sidebar" />
      </a>

      <div class="user">
        <a class="user-menu-button" :class="{ open: dropdownOpen }" @click="toggleDropdown()">
          <Icon
            icon="caret-down"
            :additionalClasses="{ caret: true }"
            :width="6"
            :height="3"
            :svg="true"
          />

          <!-- Span class name is used in bugsnag config, must be updated there if changed -->
          <span class="name">
            <slot name="name" />
          </span>
        </a>

        <ul class="dropdown" :class="{ open: dropdownOpen }">
          <li>
            <slot name="profile-link" />
          </li>

          <li>
            <slot name="logout" />
          </li>
        </ul>
      </div>
    </div>
    <div class="overlay" v-if="dropdownOpen" @click="closeDropdown()" />
  </div>
</template>

<style lang="scss" scoped>
.overlay {
  z-index: 998;
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
}

.topbar {
  width: 100%;
  height: $topbar-height;
  padding: 0 $space-16;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;

  :deep(.icon) {
    display: block;
  }

  @include tablet-large {
    border-bottom: 1px solid $secondary-border;
    padding: 0 $space-24;
  }

  .logo {
    display: none;

    @include tablet-large {
      display: block;
      width: $logo-width;
    }
  }

  .toggle-sidebar {
    @include tablet-large {
      display: none;
    }
  }

  .user {
    position: relative;

    .user-menu-button {
      color: $primary-text;
      background: 0;
      border: 0;
      display: flex;
      text-align: right;
      flex-direction: column;
      padding-right: $space-16;
      position: relative;
      justify-content: center;

      :deep(.caret) {
        path {
          fill: $tertiary-foreground;
        }
        position: absolute;
        right: 0;
      }
    }

    .dropdown {
      background-color: $primary-background;
      border-radius: 6px;
      box-shadow: 0px 10px 54px rgba(0, 0, 0, 0.1);
      list-style: none;
      margin: 0;
      min-width: 190px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      right: 0;
      z-index: 999;
      opacity: 0;
      visibility: hidden;
      transition: $transition-duration;

      &.open {
        visibility: visible;
        opacity: 1;
        margin-top: 10px;
      }

      li {
        :deep(a),
        :deep(button) {
          @include secondary-text-medium;
          color: $primary-text;
          background: $primary-background;
          text-align: left;
          padding: $space-16;
          display: block;
          width: 100%;
          border-bottom: 1px solid $primary-border;
          transition: $transition-duration;
          cursor: pointer;

          &:hover,
          &:focus {
            background-color: $ghost-button-hover;
            outline: 0;
          }
        }

        &:last-of-type {
          :deep(a),
          :deep(button) {
            border: 0;
          }
        }
      }
    }
  }
}
</style>
